export const CONSUMER_CASE_TAXONOMY_CONFIG = {
  id: 'consumer_protection',
  label: 'Consumer Protection',
  shortLabel: 'Consumer',
  description: 'Consumer complaints, defective goods, deficiency in services, professional negligence, builder/developer disputes, utility/telecom/online service complaints, appeals, execution/compliance, and related consumer proceedings.',
  active: true,
  types: [
    {
      id: 'Consumer Complaint',
      label: 'Consumer Complaint',
      shortLabel: 'Complaint',
      description: 'General consumer complaint against a manufacturer, seller, service provider, trader, company, professional, or business for defective goods, deficient services, unfair practice, overcharging, non-delivery, misrepresentation, or consumer loss.',
      legalBasis: [
        'Complaint may require prior notice/legal notice depending on applicable consumer law.',
        'Limitation may apply and differs by applicable statute.',
        'Consumer must show product/service relationship, defect/deficiency, loss, and relief claimed.',
        'Some matters may belong before regulator/special forum instead of consumer court.'
      ],
      provinceAwareLegalBasis: {
        'Punjab': 'Punjab Consumer Protection Act, 2005. Consumer Court is common forum; pre-claim notice may be required.',
        'Khyber Pakhtunkhwa': 'Khyber Pakhtunkhwa Consumer Protection Act, 1997.',
        'Sindh': 'Sindh Consumer Protection Act, 2014.',
        'Islamabad Capital Territory': 'Islamabad Consumers Protection Act, 1995.',
        'Balochistan': 'Balochistan Consumer Protection Act, 2003.'
      },
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Primary forum for consumer complaints under applicable consumer law.' },
        { id: 'authority', label: 'Consumer Protection Authority / Council', description: 'Use where administrative complaint or consumer authority process is relevant.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Use where dispute is outside consumer law or requires ordinary civil remedy.' },
        { id: 'regulator', label: 'Regulator / Special Authority', description: 'Use where sector-specific forum exists.' },
        { id: 'high_court', label: 'High Court / Constitutional Jurisdiction', description: 'Use where writ challenge is maintainable.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Consumer forum depends on province/territory, cause of action, nature of goods/service, respondent, notice requirement, limitation, and any special regulator.',
      subtypes: [
        'General Consumer Complaint',
        'Defective Product Complaint',
        'Deficiency in Service Complaint',
        'Misrepresentation / False Claim',
        'Unfair Trade Practice',
        'Overcharging / Excessive Billing',
        'Non-Delivery of Goods',
        'Delay in Service',
        'Refund / Replacement Claim',
        'Warranty / Guarantee Claim',
        'Other Consumer Complaint',
        'Other'
      ],
      sideOptions: ['Consumer / Complainant', 'Respondent / Service Provider', 'Manufacturer', 'Seller', 'Dealer', 'Company', 'Professional'],
      documentTabs: ['All', 'Consumer Complaint', 'Notice / Correspondence', 'Purchase / Service Record', 'Evidence', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Consumer Record Review', 'Notice / Pre-Claim Correspondence', 'Complaint Drafting', 'Filing before Consumer Forum', 'Scrutiny / Objections', 'Notice to Respondent', 'Written Reply', 'Settlement / Mediation / Pre-Trial', 'Evidence / Expert Report', 'Arguments', 'Order / Judgment', 'Compliance / Execution / Appeal', 'Closed'],
      aiContextHints: 'AI should understand consumer, respondent, goods/service, purchase/service date, notice, defect/deficiency, loss, compensation, refund/replacement/repair, evidence, and limitation/notice issues.'
    },
    {
      id: 'Defective Goods Claim',
      label: 'Defective Goods Claim',
      shortLabel: 'Defective Goods',
      description: 'Consumer claim relating to defective, unsafe, substandard, expired, counterfeit, mislabelled, damaged, or non-conforming goods.',
      legalBasis: [
        'Use applicable consumer protection law.',
        'Goods/product defect, warranty, invoice, expert evidence, and loss are central.',
        'Unsafe/expired/counterfeit products may also involve regulator/authority action.'
      ],
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Primary forum for defective goods claim.' },
        { id: 'authority', label: 'Consumer Protection Authority / Council', description: 'Use where administrative action, unsafe product, labelling, or market practice complaint is relevant.' },
        { id: 'regulator', label: 'Regulator / Special Authority', description: 'Use where product falls under a sector regulator or standards authority.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Use where matter is outside consumer forum jurisdiction.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Defective goods forum depends on product type, consumer law, warranty, respondent, and whether regulatory action is required.',
      subtypes: [
        'Defective Product',
        'Unsafe Product',
        'Expired Product',
        'Counterfeit / Fake Product',
        'Mislabelled Product',
        'Damaged Goods',
        'Substandard Goods',
        'Warranty Defect',
        'Non-Conforming Product',
        'Defective Vehicle / Auto Product',
        'Defective Electronics / Appliance',
        'Defective Construction Material',
        'Other Defective Goods Claim',
        'Other'
      ],
      sideOptions: ['Consumer / Complainant', 'Seller / Respondent', 'Manufacturer', 'Distributor', 'Dealer', 'Service Centre'],
      documentTabs: ['All', 'Defective Goods Complaint', 'Purchase Record', 'Warranty / Guarantee', 'Defect Evidence', 'Expert / Inspection Report', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Purchase / Warranty Review', 'Defect Evidence Collection', 'Notice / Complaint to Seller or Manufacturer', 'Complaint Drafting', 'Filing', 'Notice to Respondent', 'Reply / Defence', 'Expert Inspection / Report', 'Evidence', 'Arguments', 'Order for Refund / Replacement', 'Compliance / Appeal', 'Closed'],
      aiContextHints: 'AI should understand product, purchase date, invoice, defect, warranty, repair attempts, expert report, seller/manufacturer, loss, and relief.'
    },
    {
      id: 'Deficiency in Service Claim',
      label: 'Deficiency in Service Claim',
      shortLabel: 'Service Deficiency',
      description: 'Consumer claim relating to deficient, delayed, negligent, incomplete, poor-quality, promised but undelivered, wrongly billed, or overcharged services.',
      legalBasis: [
        'Use applicable consumer protection law.',
        'Service contract/booking, payment proof, correspondence, deficiency, loss, and relief claimed are central.',
        'Sector-specific regulators may be relevant.'
      ],
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Primary forum for service deficiency claim.' },
        { id: 'authority', label: 'Consumer Protection Authority / Council', description: 'Use where administrative consumer complaint is relevant.' },
        { id: 'regulator', label: 'Regulator / Special Authority', description: 'Use where telecom, utility, travel, education, health, banking, insurance, or another regulator applies.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Use where matter is contractual/civil and outside consumer jurisdiction.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Service deficiency forum depends on service type, respondent, regulator, consumer law, and nature of loss.',
      subtypes: [
        'Delayed Service',
        'Non-Performance of Service',
        'Poor Quality Service',
        'Incomplete Service',
        'Wrong Billing / Overbilling',
        'Failure to Repair',
        'Failure to Honour Warranty',
        'Travel / Airline / Transport Service Complaint',
        'Hotel / Event / Hospitality Complaint',
        'Education / Coaching Service Complaint',
        'Courier / Delivery Service Complaint',
        'Other Service Deficiency',
        'Other'
      ],
      sideOptions: ['Consumer / Complainant', 'Service Provider / Respondent', 'Company', 'Agency', 'Contractor', 'Professional'],
      documentTabs: ['All', 'Service Complaint', 'Contract / Booking Record', 'Billing / Payment Record', 'Correspondence', 'Evidence', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Service Record Review', 'Deficiency / Loss Evidence Collection', 'Notice / Complaint to Service Provider', 'Complaint Drafting', 'Filing', 'Notice to Respondent', 'Reply / Defence', 'Evidence / Expert Report', 'Arguments', 'Order / Compensation / Refund', 'Compliance / Appeal', 'Closed'],
      aiContextHints: 'AI should understand service promised, service delivered, delay/deficiency, payment, correspondence, bill dispute, loss, regulator issue, and relief.'
    },
    {
      id: 'Medical Negligence / Professional Service Complaint',
      label: 'Medical Negligence / Professional Service Complaint',
      shortLabel: 'Professional Negligence',
      description: 'Consumer complaint relating to deficient professional service, medical negligence, hospital service issues, diagnostic errors, professional misconduct, or negligent service by a qualified service provider.',
      legalBasis: [
        'Use applicable consumer law where professional service qualifies.',
        'Medical negligence may also involve healthcare commission, medical regulator, hospital authority, or civil damages route.',
        'Do not overstate consumer forum as the only route.'
      ],
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Use where professional/medical service deficiency is maintainable under consumer law.' },
        { id: 'healthcare_comm', label: 'Healthcare Commission / Medical Regulator', description: 'Use where healthcare-specific complaint forum is relevant.' },
        { id: 'prof_reg', label: 'Professional Regulatory Body', description: 'Use where professional misconduct/disciplinary complaint belongs before a regulator.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Use for damages/negligence claim where ordinary civil route is appropriate.' },
        { id: 'high_court', label: 'High Court / Constitutional Jurisdiction', description: 'Use where writ/constitutional challenge is maintainable.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Professional negligence forum depends on service type, respondent, consumer law, regulator, evidence, and nature of relief.',
      subtypes: [
        'Medical Negligence',
        'Hospital Service Deficiency',
        'Wrong Diagnosis / Lab Report Issue',
        'Surgical / Treatment Negligence',
        'Dental / Clinic Negligence',
        'Pharmacy / Medicine Issue',
        'Legal / Professional Service Deficiency',
        'Engineering / Architect Service Deficiency',
        'Consultancy Service Deficiency',
        'Other Professional Service Complaint',
        'Other'
      ],
      sideOptions: ['Consumer / Patient / Client', 'Hospital / Clinic', 'Doctor / Professional', 'Lab / Diagnostic Centre', 'Pharmacy', 'Service Provider'],
      documentTabs: ['All', 'Professional Complaint', 'Medical / Professional Record', 'Bills / Payment Record', 'Expert Evidence', 'Correspondence', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Service / Treatment Record Review', 'Expert / Medical Record Collection', 'Notice / Complaint to Service Provider', 'Complaint Drafting', 'Filing', 'Notice to Respondent', 'Reply / Defence', 'Expert Opinion / Medical Board / Inspection', 'Evidence', 'Arguments', 'Order / Compensation / Direction', 'Compliance / Appeal / Regulator Action', 'Closed'],
      aiContextHints: 'AI should understand professional relationship, service provided, alleged negligence, medical/professional record, expert evidence, loss/injury, causation, compensation, and regulator route.'
    },
    {
      id: 'Builder / Developer / Housing Society Complaint',
      label: 'Builder / Developer / Housing Society Complaint',
      shortLabel: 'Builder / Housing',
      description: 'Consumer complaint against builder, developer, housing society, property project, contractor, or real estate service provider for delay, non-delivery, defective construction, misrepresentation, overcharging, or failure to provide promised facilities.',
      legalBasis: [
        'Use consumer law where purchaser/allottee qualifies as consumer and services/goods deficiency is alleged.',
        'Housing authority, development authority, cooperative society, civil court, or regulatory forum may also be relevant depending facts.',
        'Do not overstate consumer forum as the only route.'
      ],
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Use where complaint is maintainable as consumer/service deficiency.' },
        { id: 'housing_auth', label: 'Housing / Development Authority', description: 'Use where regulatory/project approval or development authority complaint is relevant.' },
        { id: 'coop_soc', label: 'Cooperative / Society Forum', description: 'Use where housing society/cooperative forum is relevant.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Use where title, possession, declaration, specific performance, or civil rights are central.' },
        { id: 'high_court', label: 'High Court / Constitutional Jurisdiction', description: 'Use where public authority or writ issue is involved.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Builder/housing forum depends on allotment status, project authority, consumer relationship, title/possession issue, and relief sought.',
      subtypes: [
        'Delay in Possession / Delivery',
        'Non-Delivery of Plot / Unit',
        'Defective Construction',
        'Failure to Provide Facilities',
        'Misrepresentation in Project',
        'Overcharging / Hidden Charges',
        'Refund of Booking Amount',
        'Transfer / Allotment Dispute',
        'Development Charges Dispute',
        'Maintenance Charges Dispute',
        'Other Builder / Housing Complaint',
        'Other'
      ],
      sideOptions: ['Consumer / Allottee / Purchaser', 'Builder / Developer', 'Housing Society', 'Contractor', 'Real Estate Company', 'Project Management'],
      documentTabs: ['All', 'Housing Complaint', 'Booking / Allotment Record', 'Payment Record', 'Project / Construction Record', 'Correspondence', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Booking / Allotment Record Review', 'Payment / Possession Timeline Review', 'Notice / Complaint to Builder or Society', 'Complaint Drafting', 'Filing', 'Notice to Respondent', 'Reply / Defence', 'Project / Payment Evidence', 'Expert / Inspection Report', 'Arguments', 'Order / Refund / Delivery', 'Compliance / Appeal', 'Closed'],
      aiContextHints: 'AI should understand project, booking/allotment, payment schedule, possession due date, delay, promised facilities, construction defect, overcharging, refund, and compensation.'
    },
    {
      id: 'Utility / Telecom / Online Service Complaint',
      label: 'Utility / Telecom / Online Service Complaint',
      shortLabel: 'Utility / Online',
      description: 'Consumer complaint relating to electricity, gas, water, telecom, internet, mobile service, online marketplace, digital service, e-commerce, or app-based service issues.',
      legalBasis: [
        'Use consumer law where maintainable.',
        'Sector regulators may be relevant (e.g. PTA, NEPRA, OGRA).'
      ],
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Use where utility/telecom/online service complaint is maintainable under consumer law.' },
        { id: 'regulator', label: 'Regulator / Special Authority', description: 'Use where sector-specific regulator or complaint authority should be approached.' },
        { id: 'internal_comp', label: 'Company Internal Complaint Forum', description: 'Use where internal complaint escalation is required or useful before litigation.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Use where ordinary civil/contractual remedy is appropriate.' },
        { id: 'high_court', label: 'High Court / Constitutional Jurisdiction', description: 'Use where public authority/regulator action or writ issue is involved.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Utility/telecom/online forum depends on service type, regulator, consumer law, internal complaint process, and relief sought.',
      subtypes: [
        'Electricity (NEPRA)',
        'Gas (OGRA)',
        'Telecom / Internet (PTA)',
        'Water / Municipal',
        'Online Marketplace',
        'Other Utility / Service',
        'Electricity Billing Complaint',
        'Gas Billing Complaint',
        'Water / Municipal Service Complaint',
        'Telecom / Mobile Service Complaint',
        'Internet Service Complaint',
        'Online Purchase / E-Commerce Complaint',
        'App-Based Service Complaint',
        'Digital Subscription Complaint',
        'Unauthorized Charges',
        'Non-Delivery / Wrong Delivery',
        'Other Utility / Online Service Complaint',
        'Other'
      ],
      sideOptions: ['Consumer / Complainant', 'Utility Company', 'Telecom Company', 'Internet Provider', 'Online Seller', 'Marketplace / Platform', 'Service Provider'],
      documentTabs: ['All', 'Utility / Online Complaint', 'Bills / Payment Record', 'Order / Service Record', 'Correspondence', 'Regulator Record', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Bill / Service / Order Review', 'Internal Complaint / Notice', 'Regulator Complaint, if applicable', 'Consumer Complaint Drafting', 'Filing', 'Notice to Respondent', 'Reply / Defence', 'Record / Billing Evidence / Delivery Evidence', 'Arguments', 'Order / Correction / Refund', 'Compliance / Appeal', 'Closed'],
      aiContextHints: 'AI should understand service provider, bill/order number, disputed charges, service failure, internal complaint, regulator complaint, payment proof, refund/restoration/correction request, and compensation.'
    },
    {
      id: 'Consumer Appeal',
      label: 'Consumer Appeal',
      shortLabel: 'Appeal',
      description: 'Appeal against Consumer Court, Consumer Protection Authority, District Consumer Court, or other consumer forum order where maintainable.',
      legalBasis: [
        'Appeal forum and limitation depend on the applicable provincial/territorial consumer law and impugned order.',
        'For Punjab, appeals from Consumer Court orders commonly lie to the High Court under the Act.'
      ],
      forumOptions: [
        { id: 'app_forum', label: 'Appellate Consumer Forum / High Court', description: 'Use where consumer law provides appeal to High Court or appellate forum.' },
        { id: 'dist_sess', label: 'District / Sessions Appellate Forum, where applicable', description: 'Use where applicable consumer law provides district/sessions appellate route.' },
        { id: 'auth_route', label: 'Consumer Authority / Council Appellate Route', description: 'Use where administrative consumer authority order is appealed through statutory route.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Consumer appeal forum depends on province/territory, impugned order, consumer statute, and limitation.',
      subtypes: [
        'Appeal Against Consumer Court Order',
        'Appeal Against Dismissal of Complaint',
        'Appeal Against Compensation Order',
        'Appeal Against Penalty Order',
        'Appeal Against Interim Order',
        'Appeal Against Execution / Compliance Order',
        'Other Consumer Appeal',
        'Other'
      ],
      sideOptions: ['Appellant', 'Respondent', 'Consumer', 'Service Provider', 'Manufacturer', 'Seller'],
      documentTabs: ['All', 'Memo of Appeal', 'Applications', 'Impugned Order', 'Consumer Case Record', 'Orders', 'Miscellaneous'],
      defaultStages: ['Impugned Order Review', 'Certified Copy Awaited', 'Memo of Appeal Drafting', 'Filing', 'Scrutiny / Objections', 'Admission / Preliminary Hearing', 'Notice to Respondent', 'Record Summoned / Received', 'Stay / Suspension Application', 'Arguments', 'Judgment / Final Order', 'Further Remedy / Compliance', 'Closed'],
      aiContextHints: 'AI should understand impugned consumer order, complaint facts, grounds of appeal, compensation/refund/penalty, limitation, stay, and relief sought.'
    },
    {
      id: 'Consumer Execution / Compliance',
      label: 'Consumer Execution / Compliance',
      shortLabel: 'Execution / Compliance',
      description: 'Proceedings for implementation, enforcement, execution, or compliance of Consumer Court or consumer forum order, including payment of compensation, refund, replacement, repair, corrective action, or penalty compliance.',
      legalBasis: [
        'Use applicable consumer law execution/compliance provisions.'
      ],
      forumOptions: [
        { id: 'same_court', label: 'Same Consumer Court / Forum That Passed the Order', description: 'Common forum for compliance/execution of consumer order.' },
        { id: 'authority', label: 'Consumer Protection Authority / Council', description: 'Use where administrative enforcement or authority compliance route applies.' },
        { id: 'app_forum', label: 'Appellate Forum / High Court', description: 'Use where compliance issue is connected with appeal or higher court order.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Consumer execution/compliance forum depends on the original order, applicable consumer law, and whether appeal/stay is pending.',
      subtypes: [
        'Execution of Consumer Court Order',
        'Compliance of Compensation Order',
        'Compliance of Refund Order',
        'Compliance of Replacement Order',
        'Compliance of Repair / Service Direction',
        'Enforcement of Penalty Order',
        'Non-Compliance Application',
        'Other Consumer Compliance Matter',
        'Other'
      ],
      sideOptions: ['Decree Holder / Consumer', 'Judgment Debtor / Respondent', 'Applicant', 'Respondent', 'Service Provider', 'Manufacturer', 'Seller'],
      documentTabs: ['All', 'Execution / Compliance Application', 'Consumer Order', 'Compliance Record', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: ['Consumer Order Review', 'Compliance Demand / Notice', 'Execution / Compliance Application Drafting', 'Filing', 'Notice to Judgment Debtor / Respondent', 'Reply / Compliance Report', 'Hearing', 'Compliance / Recovery / Enforcement Order', 'Partial / Full Compliance', 'Disposal / Further Remedy', 'Closed'],
      aiContextHints: 'AI should understand original order, compensation/refund/replacement direction, respondent, compliance deadline, non-compliance evidence, payment status, and enforcement relief.'
    },
    {
      id: 'Consumer Miscellaneous Application',
      label: 'Consumer Miscellaneous Application',
      shortLabel: 'Application',
      description: 'Connected or standalone applications in consumer proceedings, including stay, restoration, condonation of delay, production of record, expert inspection, impleadment, amendment, early hearing, or execution-related applications.',
      forumOptions: [
        { id: 'same_court', label: 'Same Consumer Court / Forum Where Case Is Pending', description: 'For applications connected with pending consumer complaint/execution.' },
        { id: 'app_forum', label: 'Appellate Consumer Forum / High Court', description: 'For applications connected with consumer appeal.' },
        { id: 'authority', label: 'Consumer Protection Authority / Council', description: 'For applications connected with administrative consumer proceedings.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      forumHelper: 'Application forum depends on the main consumer case, pending forum, province/territory, and nature of application.',
      subtypes: [
        'Stay Application',
        'Application for Condonation of Delay',
        'Application for Restoration',
        'Application for Setting Aside Ex Parte Order',
        'Application for Production of Record',
        'Application for Expert Inspection / Report',
        'Application for Impleadment',
        'Application for Amendment',
        'Application for Early Hearing',
        'Application for Compliance / Implementation',
        'Other Consumer Application',
        'Other'
      ],
      sideOptions: ['Applicant', 'Respondent', 'Consumer', 'Service Provider', 'Manufacturer', 'Seller'],
      documentTabs: ['All', 'Application', 'Reply / Objections', 'Supporting Documents', 'Consumer Case Record', 'Orders', 'Miscellaneous'],
      defaultStages: ['Application Preparation', 'Filing', 'Scrutiny / Objections', 'Notice to Respondent', 'Reply / Objections', 'Expert / Record Direction', 'Hearing / Arguments', 'Order', 'Compliance / Main Case Linkage', 'Closed'],
      aiContextHints: 'AI should understand application subtype, main consumer case, product/service issue, pending forum, urgency, expert report need, delay/restoration issue, and order sought.'
    },
    {
      id: 'Other Consumer Matter',
      label: 'Other Consumer Matter',
      shortLabel: 'Other Consumer',
      description: 'Use this for consumer, customer, product, service, professional service, or market-related matters that do not fit the available consumer case types.',
      forumOptions: [
        { id: 'consumer_court', label: 'Consumer Court / Competent Consumer Forum', description: 'Primary consumer forum.' },
        { id: 'authority', label: 'Consumer Protection Authority / Council', description: 'Administrative consumer forum.' },
        { id: 'regulator', label: 'Regulator / Special Authority', description: 'Specialized regulator.' },
        { id: 'civil_court', label: 'Civil Court', description: 'Civil jurisdiction.' },
        { id: 'high_court', label: 'High Court / Constitutional Jurisdiction', description: 'Constitutional / Writ jurisdiction.' },
        { id: 'other', label: 'Other / Custom Forum', description: 'Custom forum.' }
      ],
      forumHelper: 'Forum depends on province/territory, applicable consumer law, notice/limitation, product/service type, respondent, regulator, and relief sought.',
      subtypes: [
        'Custom Consumer Matter',
        'Custom Service Complaint',
        'Custom Product Complaint',
        'Other'
      ],
      sideOptions: ['Party A', 'Party B', 'Consumer', 'Service Provider', 'Manufacturer', 'Seller', 'Applicant', 'Respondent'],
      documentTabs: ['All', 'Documents', 'Applications', 'Evidence', 'Orders', 'Miscellaneous'],
      defaultStages: ['Case Preparation / Record Review', 'Notice / Complaint', 'Filing / Institution', 'Notice to Respondent', 'Reply / Response', 'Evidence / Record / Expert Report', 'Arguments / Hearing', 'Order / Judgment', 'Compliance / Further Remedy', 'Closed'],
      aiContextHints: 'AI should rely on user-provided description, goods/service details, forum, uploaded documents, and selected relief.'
    }
  ]
};

export const normalizeConsumerCaseClassification = (existingCase: any) => {
  if (!existingCase) return existingCase;

  const labels = [
    existingCase.caseCategory,
    existingCase.caseType,
    existingCase.matterType,
    existingCase.subType,
    existingCase.caseSubtype,
    ...(existingCase.tags || [])
  ].filter(Boolean).map(s => s.toLowerCase());

  const hasLabel = (term: string) => labels.some(l => l.includes(term.toLowerCase()));

  // If already mapped to new structure
  if (existingCase.caseCategory === 'Consumer Protection') {
    return existingCase;
  }

  // Check if it's a consumer case
  const isConsumer = hasLabel('consumer') || hasLabel('defective product') || hasLabel('service complaint') || hasLabel('medical negligence') || hasLabel('defective goods') || hasLabel('warranty claim') || hasLabel('expired product') || hasLabel('fake product') || hasLabel('wrong billing') || hasLabel('overbilling') || hasLabel('housing society complaint') || hasLabel('utility complaint') || hasLabel('telecom complaint') || hasLabel('online purchase') || hasLabel('e-commerce');
  
  if (!isConsumer) return existingCase; // Not a consumer case

  const newCase = { ...existingCase, legacyCaseType: existingCase.caseType || existingCase.matterType };
  newCase.caseCategory = 'Consumer Protection';
  
  if (hasLabel('defective product') || hasLabel('defective goods') || hasLabel('warranty claim') || hasLabel('expired product') || hasLabel('fake product') || hasLabel('defective vehicle') || hasLabel('defective electronics')) {
    newCase.caseType = 'Defective Goods Claim';
    if (hasLabel('warranty') || hasLabel('warranty defect')) newCase.caseSubtype = 'Warranty Defect';
    else if (hasLabel('expired')) newCase.caseSubtype = 'Expired Product';
    else if (hasLabel('fake') || hasLabel('counterfeit')) newCase.caseSubtype = 'Counterfeit / Fake Product';
    else if (hasLabel('vehicle') || hasLabel('auto')) newCase.caseSubtype = 'Defective Vehicle / Auto Product';
    else if (hasLabel('electronic') || hasLabel('appliance')) newCase.caseSubtype = 'Defective Electronics / Appliance';
    else newCase.caseSubtype = 'Defective Product';
  } else if (hasLabel('service complaint') || hasLabel('deficiency in service') || hasLabel('delayed service') || hasLabel('wrong billing') || hasLabel('overbilling') || hasLabel('courier') || hasLabel('travel')) {
    newCase.caseType = 'Deficiency in Service Claim';
    if (hasLabel('delayed')) newCase.caseSubtype = 'Delayed Service';
    else if (hasLabel('wrong billing') || hasLabel('overbilling')) newCase.caseSubtype = 'Wrong Billing / Overbilling';
    else if (hasLabel('courier') || hasLabel('delivery')) newCase.caseSubtype = 'Courier / Delivery Service Complaint';
    else if (hasLabel('travel') || hasLabel('airline') || hasLabel('transport')) newCase.caseSubtype = 'Travel / Airline / Transport Service Complaint';
    else newCase.caseSubtype = 'Deficiency in Service Complaint';
  } else if (hasLabel('medical negligence') || hasLabel('hospital negligence') || hasLabel('lab report') || hasLabel('professional negligence')) {
    newCase.caseType = 'Medical Negligence / Professional Service Complaint';
    if (hasLabel('medical negligence')) newCase.caseSubtype = 'Medical Negligence';
    else if (hasLabel('hospital')) newCase.caseSubtype = 'Hospital Service Deficiency';
    else if (hasLabel('lab report') || hasLabel('diagnosis')) newCase.caseSubtype = 'Wrong Diagnosis / Lab Report Issue';
    else newCase.caseSubtype = 'Other Professional Service Complaint';
  } else if (hasLabel('builder complaint') || hasLabel('housing society complaint') || hasLabel('delay in possession') || hasLabel('non-delivery of plot') || hasLabel('defective construction')) {
    newCase.caseType = 'Builder / Developer / Housing Society Complaint';
    if (hasLabel('delay in possession')) newCase.caseSubtype = 'Delay in Possession / Delivery';
    else if (hasLabel('non-delivery')) newCase.caseSubtype = 'Non-Delivery of Plot / Unit';
    else if (hasLabel('defective construction')) newCase.caseSubtype = 'Defective Construction';
    else newCase.caseSubtype = 'Other Builder / Housing Complaint';
  } else if (hasLabel('utility complaint') || hasLabel('electricity bill') || hasLabel('gas bill') || hasLabel('telecom complaint') || hasLabel('internet complaint') || hasLabel('online purchase') || hasLabel('e-commerce')) {
    newCase.caseType = 'Utility / Telecom / Online Service Complaint';
    if (hasLabel('electricity')) newCase.caseSubtype = 'Electricity Billing Complaint';
    else if (hasLabel('gas')) newCase.caseSubtype = 'Gas Billing Complaint';
    else if (hasLabel('telecom') || hasLabel('mobile')) newCase.caseSubtype = 'Telecom / Mobile Service Complaint';
    else if (hasLabel('internet')) newCase.caseSubtype = 'Internet Service Complaint';
    else if (hasLabel('online purchase') || hasLabel('e-commerce')) newCase.caseSubtype = 'Online Purchase / E-Commerce Complaint';
    else newCase.caseSubtype = 'Other Utility / Online Service Complaint';
  } else if (hasLabel('consumer appeal') || hasLabel('appeal against consumer') || hasLabel('appeal against dismissal')) {
    newCase.caseType = 'Consumer Appeal';
    if (hasLabel('dismissal')) newCase.caseSubtype = 'Appeal Against Dismissal of Complaint';
    else if (hasLabel('consumer court order') || hasLabel('consumer order')) newCase.caseSubtype = 'Appeal Against Consumer Court Order';
    else newCase.caseSubtype = 'Other Consumer Appeal';
  } else if (hasLabel('consumer execution') || hasLabel('consumer compliance') || hasLabel('execution of consumer order') || hasLabel('non-compliance of consumer order')) {
    newCase.caseType = 'Consumer Execution / Compliance';
    if (hasLabel('execution of consumer order')) newCase.caseSubtype = 'Execution of Consumer Court Order';
    else if (hasLabel('non-compliance')) newCase.caseSubtype = 'Non-Compliance Application';
    else newCase.caseSubtype = 'Other Consumer Compliance Matter';
  } else if (hasLabel('consumer miscellaneous') || hasLabel('stay in consumer') || hasLabel('restoration in consumer') || hasLabel('expert inspection')) {
    newCase.caseType = 'Consumer Miscellaneous Application';
    if (hasLabel('stay')) newCase.caseSubtype = 'Stay Application';
    else if (hasLabel('restoration')) newCase.caseSubtype = 'Application for Restoration';
    else if (hasLabel('expert inspection')) newCase.caseSubtype = 'Application for Expert Inspection / Report';
    else newCase.caseSubtype = 'Other Consumer Application';
  } else if (hasLabel('consumer case') || hasLabel('consumer complaint') || hasLabel('general consumer complaint')) {
    newCase.caseType = 'Consumer Complaint';
    if (hasLabel('general consumer complaint')) newCase.caseSubtype = 'General Consumer Complaint';
    else newCase.caseSubtype = 'General Consumer Complaint';
  } else {
    newCase.caseType = 'Other Consumer Matter';
    newCase.caseSubtype = 'Custom Consumer Matter';
  }

  return newCase;
};

export const UTILITY_SERVICE_SUBTYPE_OPTIONS = [
  'Electricity (NEPRA)',
  'Gas (OGRA)',
  'Telecom / Internet (PTA)',
  'Water / Municipal',
  'Online Marketplace',
  'Other Utility / Service',
];

export const UTILITY_REGULATOR_SUBTYPES = [
  'Electricity (NEPRA)',
  'Gas (OGRA)',
  'Telecom / Internet (PTA)',
] as const;

const UTILITY_REGULATOR_PRE_COMPLAINT_STAGE: Record<(typeof UTILITY_REGULATOR_SUBTYPES)[number], string> = {
  'Electricity (NEPRA)': 'NEPRA Pre-Complaint',
  'Gas (OGRA)': 'OGRA Pre-Complaint',
  'Telecom / Internet (PTA)': 'PTA Pre-Complaint',
};

export function getConsumerUtilityStages(subtype: string, baseStages: string[]): string[] {
  const regulatorStage = UTILITY_REGULATOR_PRE_COMPLAINT_STAGE[subtype as (typeof UTILITY_REGULATOR_SUBTYPES)[number]];
  if (!regulatorStage) return baseStages;

  const internalIdx = baseStages.findIndex((s) => s.includes('Internal Complaint'));
  const draftingIdx = baseStages.findIndex((s) => s.includes('Consumer Complaint Drafting'));
  const insertAt = internalIdx >= 0 ? internalIdx + 1 : draftingIdx >= 0 ? draftingIdx : baseStages.length;

  const next = [...baseStages];
  next.splice(insertAt, 0, regulatorStage);
  return next;
}

export function getConsumerMatterStages(matterTypeId: string, subtype?: string): string[] | undefined {
  if (matterTypeId !== 'Utility / Telecom / Online Service Complaint') return undefined;
  const type = CONSUMER_CASE_TAXONOMY_CONFIG.types.find((t) => t.id === matterTypeId);
  if (!type?.defaultStages) return undefined;
  return subtype ? getConsumerUtilityStages(subtype, type.defaultStages) : [...type.defaultStages];
}
